import {
  computed,
  type ComputedRef,
  defineAsyncComponent,
  ref,
  type Ref,
  unref,
  watchEffect,
} from 'vue'
import {
  type DesignerTier,
  allTiers,
  currentTiers,
  paidBelowIndividual2Tiers,
  paidTiers,
  type SubscriptionInterval,
  type CurrentDesigner,
} from '/@/data/designer/types'
import { toSentenceCase } from '/@/utils/misc'
import { useCurrentDesigner } from '/@/data/designer/current'
import { useRouter } from 'vue-router'
import { useTierUpgradeModal } from './modals/TierUpgradeModal'
import { useTracker } from '/@/core/tracking/api'
import { useGenericModal } from '/@/core/modals/GenericModal'
import { type GoalGroup } from './school/types'
import { usePowerUpsModal } from './modals'
import { stellaModal } from '/@/stella/StellaModal'
import { useAccountsData } from './accounts/api'
import { useLegacyTierUpgradeModal } from './modals/TierUpgradeModal'
import { useMembershipPlansData } from '/@/common/plans/api'

export type TierFeature =
  | 'AFFILIATE_LINKS'
  | 'BRANDING'
  | 'CLIENTS'
  | 'COLLABORATORS'
  | 'DESIGN_SHOP'
  | 'HD_VIZIS'
  | 'JOB_FAIR'
  | 'NO_WATERMARKS'
  | 'SHOPPABLE_PROJECTS'
  | 'DESIGN_CHALLENGES'
  | 'DAILY_PLAYS'
  | 'COMMUNITY'
  | 'REFERRAL_LINKS'
  | 'CREATE_PROJECTS'
  | 'CREATE_VIZIS'
  | 'CREATE_PALETTES'
  | 'PERKS'
  | 'UPDATE_BILLING'
  | 'VIEW_MEMBERSHIP_CARD'
  | 'DESIGN_PORTFOLIO'
  | 'ADD_TO_THINGOLOGY'
  | 'SPOAK_SIGN'
  | 'IMPORT_PRODUCTS'
  | 'BOOKMARK'
  | 'EDIT_PRODUCTS'
  | 'GIVE_KUDOS'
  | 'FOLLOW_DESIGNERS'
  | 'ADD_SOCIALS'
  | 'RECEIVE_MESSAGES'
  | 'BUSINESS_CLASSES'
  | 'DESIGN_CLASSES'
  | 'MENTORSHIP'
  | 'ONBOARDING'
  | 'ONBOARDING_BASIC'
  | 'VIZI_VERSIONS'
  | 'VIZI_ASSETS'
  | 'MAGIC_ERASES'
  | 'SHOP_LIST'
  | 'SECTION_BUDGETS'
  | 'BUDGET_MARKUPS'
  | 'TEAM_MEMBERS'
  | 'PROJECT_GENERIC_THING'
  | 'PRIVATE_PROJECT'
  | 'PROJECT_SECTIONS'
  | 'PREMIUM_FONTS'
  | 'VIEW_DESIGNS'
  | 'ADVANCED_VIZIS'
  | 'PRICE_ALERTS'
  | 'VIZI_REALISTIC_RENDERING'

type LegacyUpgradeTierFeature = Extract<
  TierFeature,
  'SECTION_BUDGETS' | 'TEAM_MEMBERS' | 'PROJECT_GENERIC_THING'
>
const legacyUpgradeEligibility: Record<
  LegacyUpgradeTierFeature,
  DesignerTier[]
> = {
  SECTION_BUDGETS: [
    'INDIVIDUAL_1_5',
    'INDIVIDUAL_2',
    'PROFESSIONAL_1',
    'PROFESSIONAL_2',
    'DESIGNER',
    'GROWTH',
    'PLUS',
    'PREMIUM',
    'SUPER_1',
  ],
  TEAM_MEMBERS: ['PROFESSIONAL_1', 'PROFESSIONAL_2', 'SUPER_1'],
  PROJECT_GENERIC_THING: [
    'INDIVIDUAL_1_5',
    'INDIVIDUAL_2',
    'PROFESSIONAL_1',
    'PROFESSIONAL_2',
    'DESIGNER',
    'GROWTH',
    'PLUS',
    'PREMIUM',
    'SUPER_1',
  ],
}

// Note: Consider BASE tier eligible for everything. The specific code sites will
// need to handle BASE separately. We could try to incorporate add-on logic
// here but we haven't rolled out the base tier yet and if we do we'll be
// replacing the tier stuff with it so coming up with an api that can handle
// both seems overcomplicated.
const eligibility: Record<TierFeature, DesignerTier[]> = {
  HD_VIZIS: ['PREMIUM', 'PROFESSIONAL_2', 'SUPER_1'],
  NO_WATERMARKS: ['PREMIUM', 'PROFESSIONAL_2'],
  AFFILIATE_LINKS: [
    'DESIGNER',
    'PLUS',
    'PREMIUM',
    'PROFESSIONAL_1',
    'PROFESSIONAL_2',
    'SUPER_1',
  ],
  BRANDING: [
    'DESIGNER',
    'PLUS',
    'PREMIUM',
    'PROFESSIONAL_1',
    'PROFESSIONAL_2',
    'SUPER_1',
  ],
  CLIENTS: [
    'DESIGNER',
    'PLUS',
    'PREMIUM',
    'PROFESSIONAL_1',
    'PROFESSIONAL_2',
    'SUPER_1',
  ],
  DESIGN_SHOP: [
    'DESIGNER',
    'PLUS',
    'PREMIUM',
    'PROFESSIONAL_1',
    'PROFESSIONAL_2',
    'SUPER_1',
  ],
  JOB_FAIR: [
    'DESIGNER',
    'PLUS',
    'PREMIUM',
    'PROFESSIONAL_1',
    'PROFESSIONAL_2',
    'SUPER_1',
  ],
  SHOPPABLE_PROJECTS: [
    'GROWTH',
    'DESIGNER',
    'PLUS',
    'PREMIUM',
    'PROFESSIONAL_1',
    'PROFESSIONAL_2',
    'SUPER_1',
  ],
  BUSINESS_CLASSES: [
    'GROWTH',
    'DESIGNER',
    'PLUS',
    'PREMIUM',
    'PROFESSIONAL_1',
    'PROFESSIONAL_2',
    'SUPER_1',
  ],
  DESIGN_CLASSES: paidTiers,
  MENTORSHIP: paidTiers,
  DESIGN_CHALLENGES: allTiers,
  DAILY_PLAYS: paidTiers,
  COMMUNITY: paidTiers,
  REFERRAL_LINKS: allTiers,
  CREATE_PROJECTS: allTiers,
  PERKS: paidTiers,
  UPDATE_BILLING: paidTiers,
  VIEW_MEMBERSHIP_CARD: paidTiers,
  DESIGN_PORTFOLIO: paidTiers,
  SPOAK_SIGN: allTiers,
  ADD_SOCIALS: paidTiers,
  RECEIVE_MESSAGES: paidTiers,
  IMPORT_PRODUCTS: allTiers,
  BOOKMARK: allTiers,
  EDIT_PRODUCTS: allTiers,
  GIVE_KUDOS: allTiers,
  FOLLOW_DESIGNERS: allTiers,
  ADD_TO_THINGOLOGY: allTiers,
  CREATE_VIZIS: allTiers,
  CREATE_PALETTES: allTiers,
  ONBOARDING: paidTiers,
  ONBOARDING_BASIC: allTiers,
  VIZI_VERSIONS: paidTiers,
  COLLABORATORS: paidTiers,
  VIZI_ASSETS: paidBelowIndividual2Tiers,
  MAGIC_ERASES: paidBelowIndividual2Tiers,
  SHOP_LIST: paidTiers,
  BUDGET_MARKUPS: [
    'DESIGNER',
    'PLUS',
    'PREMIUM',
    'PROFESSIONAL_1',
    'PROFESSIONAL_2',
    'SUPER_1',
  ],
  PRIVATE_PROJECT: [
    'GROWTH',
    'DESIGNER',
    'PLUS',
    'PREMIUM',
    'PROFESSIONAL_1',
    'PROFESSIONAL_2',
    'SUPER_1',
  ],
  PROJECT_SECTIONS: paidTiers,
  PREMIUM_FONTS: paidTiers,
  VIEW_DESIGNS: paidTiers,
  ADVANCED_VIZIS: allTiers,
  PRICE_ALERTS: allTiers,
  VIZI_REALISTIC_RENDERING: paidTiers,
  ...legacyUpgradeEligibility,
}

const freeTierExperimentFeatures: TierFeature[] = [
  'DAILY_PLAYS',
  'VIEW_DESIGNS',
]

export function useTiers() {
  const { designer, isPaying, isEnterpriseMember } = useCurrentDesigner()
  const { membershipPlans } = useMembershipPlansData(computed(() => ({})))
  const membershipPlanTiers = computed<DesignerTier[]>(() =>
    membershipPlans.value.reduce((acc, plan) => {
      if (!acc.includes(plan.level)) acc.push(plan.level)
      return acc
    }, [] as DesignerTier[]),
  )

  function eligibleTiersForFeature(feature: TierFeature) {
    return eligibility[feature]
  }

  function isEligible(
    feature: TierFeature,
    designer:
      | Pick<CurrentDesigner, 'tier' | 'freeTierV1Experiment'>
      | undefined
      | null,
  ) {
    if (!designer?.tier) return false
    if (
      designer.tier === 'FREE' &&
      designer.freeTierV1Experiment &&
      freeTierExperimentFeatures.includes(feature)
    ) {
      return true
    } else if (feature in legacyUpgradeEligibility) {
      const tiers =
        !isPaying.value && !isEnterpriseMember.value
          ? eligibleTiersForFeature(feature).filter(
              t => !currentTiers.includes(t),
            )
          : eligibleTiersForFeature(feature)
      return tiers.includes(designer.tier)
    } else {
      return eligibleTiersForFeature(feature).includes(designer.tier)
    }
  }

  function eligibleTiers(feature: TierFeature, includesFree: boolean = false) {
    return eligibleTiersForFeature(feature).filter(
      t =>
        membershipPlanTiers.value.includes(t) || (includesFree && t === 'FREE'),
    )
  }

  function prettyTier(t: DesignerTier, freeTierV1Experiment?: boolean) {
    if (t === 'DIY') {
      return 'DIY (Legacy)'
    } else if (t === 'PLUS') {
      return 'Pro (Legacy)'
    } else if (t === 'INDIVIDUAL_1') {
      return 'Play'
    } else if (t === 'INDIVIDUAL_1_1') {
      return 'Play'
    } else if (t === 'INDIVIDUAL_1_5') {
      return 'Plus'
    } else if (t === 'INDIVIDUAL_2') {
      return 'Power'
    } else if (t === 'PROFESSIONAL_1') {
      return 'Pro'
    } else if (t === 'PROFESSIONAL_2') {
      return 'Premium'
    } else if (t === 'SUPER_1') {
      return 'Power'
    } else if (t === 'FREE') {
      if (freeTierV1Experiment) return 'Basic'
      else return 'Shopper'
    } else {
      return `${toSentenceCase(t)} (Legacy)`
    }
  }

  function prettyEligibleTiers(feature: TierFeature) {
    const tiers = eligibleTiers(feature).map(t => prettyTier(t))

    if (tiers.length > 2) {
      return [
        tiers.slice(0, tiers.length - 1).join(', '),
        tiers[tiers.length - 1],
      ].join(', or ')
    } else if (tiers.length > 0) {
      return tiers.join(' or ')
    } else {
      return null
    }
  }

  function getNextValidTier(
    tier: DesignerTier,
    includesFree: boolean = false,
  ): DesignerTier {
    if (tier === 'FREE' && includesFree) return tier

    const index = membershipPlanTiers.value.indexOf(tier)
    if (index >= 0) {
      return tier
    } else {
      const currentIndex = allTiers.indexOf(tier)
      if (currentIndex >= 0) {
        for (let i = currentIndex + 1; i < allTiers.length; i++) {
          if (
            membershipPlanTiers.value.includes(allTiers[i]) ||
            (includesFree && allTiers[i] === 'FREE')
          ) {
            return allTiers[i]
          }
        }
      }
      return tier
    }
  }

  function getPreviousTier(tier: DesignerTier): DesignerTier {
    const index = membershipPlanTiers.value.indexOf(tier)
    if (index === 0) {
      return 'FREE'
    } else {
      const currentIndex = allTiers.indexOf(tier)
      if (currentIndex > 0) {
        for (let i = currentIndex - 1; i >= 0; i--) {
          if (membershipPlanTiers.value.includes(allTiers[i])) {
            return allTiers[i]
          }
        }
      }
      return 'FREE'
    }
  }

  function getMinimumTier(
    feature: TierFeature,
    includesFree: boolean = false,
  ): DesignerTier {
    const tierList = eligibleTiers(feature, includesFree)

    if (
      feature === 'CREATE_PROJECTS' ||
      feature === 'CREATE_PALETTES' ||
      feature === 'CREATE_VIZIS'
    ) {
      const tier = designer.value?.tier ?? 'FREE'
      if (tier === 'FREE') {
        return tierList.find(t => t !== 'FREE') ?? 'INDIVIDUAL_1'
      }

      const index = membershipPlanTiers.value.indexOf(tier)
      return index > -1 && index < membershipPlanTiers.value.length - 1
        ? membershipPlanTiers.value[index + 1]
        : membershipPlanTiers.value[membershipPlanTiers.value.length - 1]
    }

    if (!tierList) return 'PROFESSIONAL_2'
    if (tierList.includes('FREE')) return 'FREE'
    if (tierList.includes('INDIVIDUAL_1')) return 'INDIVIDUAL_1'
    if (tierList.includes('INDIVIDUAL_1_1')) return 'INDIVIDUAL_1_1'
    if (tierList.includes('INDIVIDUAL_1_5')) return 'INDIVIDUAL_1_5'
    if (tierList.includes('INDIVIDUAL_2')) return 'INDIVIDUAL_2'
    if (tierList.includes('PROFESSIONAL_1')) return 'PROFESSIONAL_1'
    if (tierList.includes('SUPER_1')) return 'SUPER_1'
    return 'PROFESSIONAL_2'
  }

  function getGoalFeature(goalGroup: GoalGroup | undefined): TierFeature {
    if (goalGroup === 'DESIGN') return 'DESIGN_CLASSES'
    return 'BUSINESS_CLASSES'
  }

  return {
    isEligible,
    eligibleTiers,
    prettyTier,
    prettyEligibleTiers,
    getMinimumTier,
    getNextValidTier,
    getPreviousTier,
    getGoalFeature,
  }
}

export type Permission = {
  hasPermission: Ref<boolean>
  loading: Ref<boolean>
  feature: TierFeature | null | undefined
  tierGuard: (
    action: () => void,
    context: { source: string; trackingProps?: Record<string, unknown> },
    modal?: HideableModal | StellaCloseableModal,
  ) => Promise<boolean>
}

type HideableModal = {
  hide: () => void
}

type StellaCloseableModal = {
  close: () => void
}

export function useMaximumVizis() {
  const { viziLimit } = useAccountsData()

  const additionalVizis = computed(() => viziLimit.value?.additional ?? 0)

  const baseVizis = computed(() => viziLimit.value?.base ?? 0)

  const vizisTowardsLimit = computed(() => viziLimit.value?.current ?? 0)

  const maximumEditableVizis = computed(
    () => viziLimit.value?.limit ?? Infinity,
  )

  const hasMaximumEditableVizis = computed(() => {
    return vizisTowardsLimit.value >= maximumEditableVizis.value
  })

  return {
    additionalVizis,
    baseVizis,
    vizisTowardsLimit,
    maximumEditableVizis,
    hasMaximumEditableVizis,
  }
}

export function useMaximumAdvancedVizis() {
  const { advancedVizisLimit } = useAccountsData()

  const additionalAdvancedVizis = computed(
    () => advancedVizisLimit.value?.additional ?? 0,
  )

  const baseAdvancedVizis = computed(() => advancedVizisLimit.value?.base ?? 0)

  const advancedVizisTowardsLimit = computed(
    () => advancedVizisLimit.value?.current ?? 0,
  )

  const maximumAdvancedVizis = computed(
    () => advancedVizisLimit.value?.limit ?? Infinity,
  )

  const hasMaximumAdvancedVizis = computed(() => {
    return advancedVizisTowardsLimit.value >= maximumAdvancedVizis.value
  })

  return {
    additionalAdvancedVizis,
    baseAdvancedVizis,
    advancedVizisTowardsLimit,
    maximumAdvancedVizis,
    hasMaximumAdvancedVizis,
  }
}

export function useMaximumPalettes() {
  const { paletteLimit } = useAccountsData()

  const basePalettes = computed(() => paletteLimit.value?.base ?? 0)

  const palettesTowardsLimit = computed(() => paletteLimit.value?.current ?? 0)

  const maximumEditablePalettes = computed(
    () => paletteLimit.value?.limit ?? Infinity,
  )

  const hasMaximumEditablePalettes = computed(() => {
    return palettesTowardsLimit.value >= maximumEditablePalettes.value
  })

  return {
    basePalettes,
    palettesTowardsLimit,
    maximumEditablePalettes,
    hasMaximumEditablePalettes,
  }
}

export function useMaximumProjects() {
  const { projectLimit } = useAccountsData()

  const additionalProjects = computed(() => projectLimit.value?.additional ?? 0)

  const baseProjects = computed(() => projectLimit.value?.base ?? 0)

  const projectsTowardsLimit = computed(() => projectLimit.value?.current ?? 0)

  const maximumProjects = computed(() => projectLimit.value?.limit ?? Infinity)

  const hasMaximumProjects = computed(() => {
    return projectsTowardsLimit.value >= maximumProjects.value
  })

  return {
    additionalProjects,
    baseProjects,
    projectsTowardsLimit,
    maximumProjects,
    hasMaximumProjects,
  }
}

export function usePermission(
  feature: TierFeature | ComputedRef<TierFeature | null | undefined>,
): Permission {
  const { designer, loading: designerLoading } = useCurrentDesigner()
  const { showTierUpgradeModal } = useTierUpgrade()
  const { hasMaximumEditableVizis } = useMaximumVizis()
  const { hasMaximumAdvancedVizis } = useMaximumAdvancedVizis()
  const { hasMaximumEditablePalettes } = useMaximumPalettes()
  const { hasMaximumProjects } = useMaximumProjects()
  const { isEligible } = useTiers()

  const hasPermission = ref<boolean>(false)
  const loading = ref<boolean>(true)

  function tierGuard(
    action: () => void,
    context: { source: string; trackingProps?: Record<string, unknown> },
    modal?: HideableModal | StellaCloseableModal,
  ): Promise<boolean> {
    const tierFeature = unref(feature)
    let isDisplayed = false
    if (!tierFeature) return Promise.reject()
    else if (loading.value) return Promise.reject()
    else if (hasPermission.value) action()
    else {
      if (modal && 'hide' in modal) modal.hide()
      else if (modal && 'close' in modal) modal.close()
      showTierUpgradeModal({
        source: context.source,
        feature: tierFeature,
        trackingProps: context.trackingProps,
        onSubmit: action,
      })
      isDisplayed = true
    }
    return Promise.resolve(isDisplayed)
  }

  function hasMaxAllowance(feature: TierFeature): boolean {
    if (feature === 'CREATE_VIZIS') {
      return hasMaximumEditableVizis.value
    } else if (feature === 'ADVANCED_VIZIS') {
      return hasMaximumAdvancedVizis.value
    } else if (feature === 'CREATE_PALETTES') {
      return hasMaximumEditablePalettes.value
    } else if (feature === 'CREATE_PROJECTS') {
      return hasMaximumProjects.value
    } else {
      return false
    }
  }

  function checkPermission() {
    if (!designer.value) {
      hasPermission.value = false
      loading.value = designerLoading.value
    } else {
      const tierFeature = unref(feature)
      if (!tierFeature) {
        hasPermission.value = false
      } else {
        hasPermission.value =
          isEligible(tierFeature, designer.value) &&
          !hasMaxAllowance(tierFeature)
      }
      loading.value = false
    }
  }

  watchEffect(checkPermission)

  return {
    hasPermission,
    loading,
    feature: unref(feature),
    tierGuard,
  }
}

export function useTierUpgrade() {
  const tierUpgradeModal = useTierUpgradeModal()
  const tracker = useTracker()
  const genericModal = useGenericModal()
  const router = useRouter()
  const { designer, isPaying, isLegacyTier } = useCurrentDesigner()
  const { reactivatingSubscriptionSchedule } = useAccountsData()
  const { getMinimumTier, getNextValidTier } = useTiers()
  const { projectsTowardsLimit, maximumProjects } = useMaximumProjects()
  const { vizisTowardsLimit, maximumEditableVizis } = useMaximumVizis()
  const { advancedVizisTowardsLimit, maximumAdvancedVizis } =
    useMaximumAdvancedVizis()
  const { openPowerUpModal } = usePowerUpsModal()
  const { openLegacyTierUpgradeModal } = useLegacyTierUpgradeModal()

  function navigateToTierUpgrade(
    context: {
      source: string
      tierFeature?: TierFeature | null
      tier?: DesignerTier
      interval?: SubscriptionInterval
      noRefresh?: boolean
      trackingProps?: Record<string, unknown>
    },
    onBack?: () => void,
    onSubmit?: () => void,
  ) {
    const tier = context.tier
      ? getNextValidTier(context.tier, !designer.value)
      : context.tier
    const upgradeType = !designer.value
      ? 'new'
      : designer.value.tier === 'FREE'
        ? 'upgrade'
        : 'update'
    tracker.send('Upgrade Clicked', {
      source: context.source,
      upgradeType,
      feature: context.tierFeature,
      tier,
      ...(context.trackingProps ?? {}),
    })
    if (!designer.value || designer.value.tier === 'FREE') {
      if (reactivatingSubscriptionSchedule.value) {
        tierUpgradeModal.show({
          tierFeature: context.tierFeature,
          upgradeType: tier,
          source: context.source,
          trackingProps: context.trackingProps,
          onBack: onBack,
          onSubmit: onSubmit,
        })
      } else {
        const type = !designer.value ? 'NEW' : 'UPGRADE'
        router.push({
          name: 'signup',
          query: {
            type,
            levels: tier,
            feature: context.tierFeature,
            sp_ft2: tier === 'FREE' ? 'true' : undefined,
          },
        })
      }
    } else if (!isPaying.value) {
      tracker.send('No Billing Info Message Shown', {
        component: context.source,
        ...(context.trackingProps ?? {}),
      })
      genericModal.show({
        title: 'Upgrade',
        content: `We don't have any active billing info on file for you. To upgrade, please contact <a class="underline" href="mailto:support@spoak.com" target="_blank">support@spoak.com</a>.`,
        contentType: 'html',
        btns: [
          {
            label: 'Cancel',
            type: 'secondary',
            action() {
              genericModal.hide()
              onBack?.()
            },
          },
        ],
      })
    } else {
      stellaModal.open({
        content: defineAsyncComponent(
          () => import('/@/common/plans/PlanChangeModalContent.vue'),
        ),
        data: {
          defaultTier: tier,
          defaultInterval: context.interval,
          noRefresh: context.noRefresh,
          onBack,
          onSubmit,
        },
      })
    }
  }

  function showTierUpgradeModal(context: {
    source: string
    feature: TierFeature
    strict?: boolean
    onBack?: () => void
    onSubmit?: () => void
    trackingProps?: Record<string, unknown>
  }) {
    const tier = designer.value?.tier
    if (
      context.feature === 'CREATE_VIZIS' &&
      tier !== 'FREE' &&
      vizisTowardsLimit.value === maximumEditableVizis.value
    ) {
      return openPowerUpModal(
        {
          types: ['ADDITIONAL_VIZI'],
          trackingProps: {
            component: context.source,
            trigger: 'automatic',
            ...(context.trackingProps ?? {}),
          },
          onSubmit: context.onSubmit,
        },
        'modal',
      )
    } else if (
      context.feature === 'ADVANCED_VIZIS' &&
      tier !== 'FREE' &&
      advancedVizisTowardsLimit.value === maximumAdvancedVizis.value
    ) {
      return openPowerUpModal(
        {
          types: ['ADDITIONAL_VIZI'],
          trackingProps: {
            component: context.source,
            trigger: 'automatic',
            ...(context.trackingProps ?? {}),
          },
          onSubmit: context.onSubmit,
        },
        'modal',
      )
    } else if (
      context.feature === 'CREATE_PROJECTS' &&
      (tier !== 'FREE' || designer.value?.freeTierV1Experiment) &&
      projectsTowardsLimit.value === maximumProjects.value
    ) {
      return openPowerUpModal(
        {
          types: ['ADDITIONAL_PROJECT'],
          trackingProps: {
            component: context.source,
            trigger: 'automatic',
            ...(context.trackingProps ?? {}),
          },
          onSubmit: context.onSubmit,
        },
        'modal',
      )
    }

    const upgradeType: DesignerTier = getMinimumTier(
      context.feature,
      !designer.value,
    )

    tracker.send('Paywall Shown', {
      component: context.source,
      tier: tier ?? 'ANONYMOUS',
      source: context.source,
      feature: context.feature,
      ...(context.trackingProps ?? {}),
    })

    if (
      tier !== 'FREE' &&
      isLegacyTier.value &&
      context.feature in legacyUpgradeEligibility
    ) {
      return openLegacyTierUpgradeModal({
        upgradeType,
        tierFeature: context.feature,
        source: context.source,
        onBack: context.onBack,
        trackingProps: context.trackingProps,
      })
    } else {
      stellaModal.close()
      if (upgradeType === 'FREE') {
        stellaModal.open({
          content: defineAsyncComponent(
            () =>
              import(
                '/@/common/modals/TierUpgradeModal/FreeUpgradeModalContent.vue'
              ),
          ),
          data: {
            feature: context.feature,
            source: context.source,
            trackingProps: context.trackingProps,
            onDismiss: context.onBack,
          },
        })
        return stellaModal
      } else {
        tierUpgradeModal.show({
          upgradeType,
          tierFeature: context.feature,
          source: context.source,
          strict: context.strict,
          trackingProps: context.trackingProps,
          onBack: context.onBack,
          onSubmit: context.onSubmit,
        })
        return tierUpgradeModal
      }
    }
  }

  return { showTierUpgradeModal, navigateToTierUpgrade }
}
